import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import GoogleSignIn from "../GoogleSignIn";
import { ChargerScene3D } from "../3D-Components";
import ultrafastLogo from "../../assests/ultrafast_logo.png";
import api from "../interceptors.js";
import "./index.css";

const LoginPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoginError("");

    const email = e.target.email.value;
    const password = e.target.password.value;

    try {
      const response = await api.post("/api/login", {
        email,
        password,
      });

      localStorage.setItem("access_token", response.data.access_token);
      navigate("/maps", { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Login failed. Please check your details and try again.";
      setLoginError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearLoginError = () => {
    if (loginError) {
      setLoginError("");
    }
  };

  return (
    <main className="auth-shell">
      <section className="auth-visual" aria-label="Ultrafast Chargers">
        <div className="auth-visual-inner">
          <img
            src={ultrafastLogo}
            alt="Ultrafast Chargers"
            className="auth-logo"
          />
          <p className="auth-kicker">Ultrafast Chargers</p>
          <h2>Charge faster. Drive smarter.</h2>
          <div className="auth-scene-wrap">
            <ChargerScene3D className="auth-scene" variant="auth" />
          </div>
          <div className="auth-stat-row">
            <span>Live stations</span>
            <span>Smart chargers</span>
            <span>Secure access</span>
          </div>
        </div>
      </section>

      <section className="auth-panel">
        <motion.div
          className="auth-card"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="auth-heading">
            <p>Cloud dashboard</p>
            <h1>Log in</h1>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {loginError && (
              <motion.div
                className="auth-error-banner"
                role="alert"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22 }}
              >
                <AlertCircle size={20} aria-hidden="true" />
                <div>
                  <strong>Unable to log in</strong>
                  <span>{loginError}</span>
                </div>
              </motion.div>
            )}

            <label className="auth-field">
              <span>Email</span>
              <div className="auth-input-wrap">
                <Mail size={18} />
                <input
                  type="text"
                  name="email"
                  placeholder="Enter email or username"
                  required
                  className="auth-input"
                  aria-invalid={loginError ? "true" : "false"}
                  onChange={clearLoginError}
                />
              </div>
            </label>

            <label className="auth-field">
              <span>Password</span>
              <div className="auth-input-wrap">
                <LockKeyhole size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter password"
                  required
                  className="auth-input"
                  aria-invalid={loginError ? "true" : "false"}
                  onChange={clearLoginError}
                />

                <button
                  type="button"
                  className="auth-visibility-button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="auth-primary-button"
            >
              {isSubmitting ? (
                <>
                  <LoaderCircle size={18} className="animate-spin" />
                  Logging in
                </>
              ) : (
                "Continue"
              )}
            </button>
          </form>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <GoogleSignIn
            onError={setLoginError}
            onSuccess={() => navigate("/maps", { replace: true })}
          />

          <Link to="/register" className="auth-link">
            Create an account
          </Link>
        </motion.div>
      </section>
    </main>
  );
};

export default LoginPage;
